/*
 * PLAYXWORLD HOME PAGE — MONOCHROME BRUTALISM
 * Hero: Bold typography + generated banner
 * Featured Games: Brutalist cards with thick borders (from API)
 * Features: Editorial magazine layout
 * How it works: Step-by-step with oversized numbers
 */
import Layout from "@/components/Layout";
import { Link } from "wouter";
import { motion } from "framer-motion";
import { useInView } from "@/hooks/useInView";
import { Zap, Shield, Clock, ArrowRight, Gamepad2, Star, Loader2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Check, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const HERO_IMG = "https://private-us-east-1.manuscdn.com/sessionFile/ensg2s7EtdwrKwwyBUN9yG/sandbox/MUeFljqOndb6xQbGvjf4Gf-img-1_1771390254000_na1fn_aGVyby1iYW5uZXI.png?x-oss-process=image/resize,w_1920,h_1920/format,webp/quality,q_80&Expires=1798761600&Policy=eyJTdGF0ZW1lbnQiOlt7IlJlc291cmNlIjoiaHR0cHM6Ly9wcml2YXRlLXVzLWVhc3QtMS5tYW51c2Nkbi5jb20vc2Vzc2lvbkZpbGUvZW5zZzJzN0V0ZHdyS3d3eUJVTjl5Ry9zYW5kYm94L01VZUZsanFPbmRiNnhRYkd2amY0R2YtaW1nLTFfMTc3MTM5MDI1NDAwMF9uYTFmbl9hR1Z5YnkxaVlXNXVaWEkucG5nP3gtb3NzLXByb2Nlc3M9aW1hZ2UvcmVzaXplLHdfMTkyMCxoXzE5MjAvZm9ybWF0LHdlYnAvcXVhbGl0eSxxXzgwIiwiQ29uZGl0aW9uIjp7IkRhdGVMZXNzVGhhbiI6eyJBV1M6RXBvY2hUaW1lIjoxNzk4NzYxNjAwfX19XX0_&Key-Pair-Id=K2HSFNDJXOU9YS&Signature=Vx-GoHbDtQqM1nOdaRLpQbFTYWUESaoj~VYBQGvuqhAXShKPIL4gJDIxDkv1zBtm2zolRWTQNUEZ8d7zML3XZ-mim1oNEeIyDTSrZUajYIlF4i4BzMHe4c9fIzR~FM4wSMPR4A6SdD7FH3B-dsirYz5IIzSK-sSoqnuUiFB2KQ5NCxNn2CZGHJyJ8jt6dyt1OwQ95yHsPdpu024r9p~rWCfxBFvy33Y9L6PV1vjD9V8bNzDDumx5x5EIzjuFKoqAOiwqnaXee8AXcbIsdg1ayHeKmEVcfBWtx8SELoKcYv8A94wUNMGr5fNyP1e3gUinaLue9EUFhZTu1y2dWQCYxw__";

const TOPUP_IMG = "https://private-us-east-1.manuscdn.com/sessionFile/ensg2s7EtdwrKwwyBUN9yG/sandbox/MUeFljqOndb6xQbGvjf4Gf-img-3_1771390254000_na1fn_dG9wdXAtc2VjdGlvbg.png?x-oss-process=image/resize,w_1920,h_1920/format,webp/quality,q_80&Expires=1798761600&Policy=eyJTdGF0ZW1lbnQiOlt7IlJlc291cmNlIjoiaHR0cHM6Ly9wcml2YXRlLXVzLWVhc3QtMS5tYW51c2Nkbi5jb20vc2Vzc2lvbkZpbGUvZW5zZzJzN0V0ZHdyS3d3eUJVTjl5Ry9zYW5kYm94L01VZUZsanFPbmRiNnhRYkd2amY0R2YtaW1nLTNfMTc3MTM5MDI1NDAwMF9uYTFmbl9kRzl3ZFhBdGMyVmpkR2x2YmcucG5nP3gtb3NzLXByb2Nlc3M9aW1hZ2UvcmVzaXplLHdfMTkyMCxoXzE5MjAvZm9ybWF0LHdlYnAvcXVhbGl0eSxxXzgwIiwiQ29uZGl0aW9uIjp7IkRhdGVMZXNzVGhhbiI6eyJBV1M6RXBvY2hUaW1lIjoxNzk4NzYxNjAwfX19XX0_&Key-Pair-Id=K2HSFNDJXOU9YS&Signature=B4qt~pn03WcewUZonZwggyIgDlnsUrzNVZBwFzJIDjDAwgyM6CXY8cwTuZDL-SkyFkmm0R9BMH6iC-om4jtNFeKOEx7f~A9pEJby3zdsj30baQavqEr~7haIOFI~wVF4K2UBVKT3lg5HnifWI9KlYkc6XknW9oXMDp61o-DHdk3QcKFOglch1BtQvoJtwfD0XK2FP4d6DHs-7tqT5RAp8X3Ee0K3FC5fGPCW7X8nXDILq5KoGpF9KWQs0pIMEnuXMUjkM6Is9XJSABNRUowgrXvxlnDmAjYWhXY5dLIQ0pTPthD0oIjCCTrJE3IB2zB-v6ekv5bh-onHmH2w-1wWcQ__";

const SPEED_IMG = "https://private-us-east-1.manuscdn.com/sessionFile/ensg2s7EtdwrKwwyBUN9yG/sandbox/MUeFljqOndb6xQbGvjf4Gf-img-4_1771390255000_na1fn_ZmVhdHVyZS1zcGVlZA.png?x-oss-process=image/resize,w_1920,h_1920/format,webp/quality,q_80&Expires=1798761600&Policy=eyJTdGF0ZW1lbnQiOlt7IlJlc291cmNlIjoiaHR0cHM6Ly9wcml2YXRlLXVzLWVhc3QtMS5tYW51c2Nkbi5jb20vc2Vzc2lvbkZpbGUvZW5zZzJzN0V0ZHdyS3d3eUJVTjl5Ry9zYW5kYm94L01VZUZsanFPbmRiNnhRYkd2amY0R2YtaW1nLTRfMTc3MTM5MDI1NTAwMF9uYTFmbl9abVZoZEhWeVpTMXpjR1ZsWkEucG5nP3gtb3NzLXByb2Nlc3M9aW1hZ2UvcmVzaXplLHdfMTkyMCxoXzE5MjAvZm9ybWF0LHdlYnAvcXVhbGl0eSxxXzgwIiwiQ29uZGl0aW9uIjp7IkRhdGVMZXNzVGhhbiI6eyJBV1M6RXBvY2hUaW1lIjoxNzk4NzYxNjAwfX19XX0_&Key-Pair-Id=K2HSFNDJXOU9YS&Signature=vAus-G-j5DqKeFt5Y-X2Yda09-5iCpLLk9sAdQc~NMVkZYUHWFyyOAl3wxGRK1DGR~3ylt0YIUD7IQf1FbVNfJ9g6dxyaaOT0yzv~O0LHFo0~t6xTrbW1awpHPCozmAOcvCSfX~5ILzUf8CtO6PR4-KKwOZerN6R5ponLT-qkRepLF2YAzy2NKVPpPIVh2ybQIkHk8zRGvqZqhUzYzlpzbmYkIKWGHM7XjlsvB73GVM~wx3U0R59yANWqBLw-brG~pNCjnWMVjx95bYJbV8pjjY-0VkWBc5Ygb3dlbnXllci-DTaqc99im~PhCLQ8wgl8XI9hViR6Vxc5s0Ey4AGzA__";

const SECURE_IMG = "https://private-us-east-1.manuscdn.com/sessionFile/ensg2s7EtdwrKwwyBUN9yG/sandbox/MUeFljqOndb6xQbGvjf4Gf-img-5_1771390265000_na1fn_ZmVhdHVyZS1zZWN1cmU.png?x-oss-process=image/resize,w_1920,h_1920/format,webp/quality,q_80&Expires=1798761600&Policy=eyJTdGF0ZW1lbnQiOlt7IlJlc291cmNlIjoiaHR0cHM6Ly9wcml2YXRlLXVzLWVhc3QtMS5tYW51c2Nkbi5jb20vc2Vzc2lvbkZpbGUvZW5zZzJzN0V0ZHdyS3d3eUJVTjl5Ry9zYW5kYm94L01VZUZsanFPbmRiNnhRYkd2amY0R2YtaW1nLTVfMTc3MTM5MDI2NTAwMF9uYTFmbl9abVZoZEhWeVpTMXpaV04xY21VLnBuZz94LW9zcy1wcm9jZXNzPWltYWdlL3Jlc2l6ZSx3XzE5MjAsaF8xOTIwL2Zvcm1hdCx3ZWJwL3F1YWxpdHkscV84MCIsIkNvbmRpdGlvbiI6eyJEYXRlTGVzc1RoYW4iOnsiQVdTOkVwb2NoVGltZSI6MTc5ODc2MTYwMH19fV19&Key-Pair-Id=K2HSFNDJXOU9YS&Signature=dB9Ckv4hRSeaj8E~9Fm~4ZlKRTqRL8RCWYB92wuSb0svVkz2gHDPXYC2jzwqdp661EoUB21eT45ni02mQwczxhFIfHqo8i80VcBNpzqeTFqTxIOg7xsYEaKIPWdPSoeVAe7krfumL7eNB-D-imYlCh8wKbLx8YeDRBOfMUSsUjmJ0eCAXCknHXqgr0SD58xT9sks3B-XZXHur3rQ5SuOvmxesHki6x1PidIlSB2MsutD7KTRT35LWwtHIrW4TZJJIdFnh~TQu3HgsYUmTv162hpNPrJjoAz7cRUidC4FsOXdWRhHze9WV7xL6hyvKrGwwEr4dTr1UUyvFy0wk-UrXQ__";

function HeroSection() {
  const { t } = useTranslation();
  return (
    <section className="relative overflow-hidden bg-black">
      {/* Hero Image Background */}
      <div className="absolute inset-0 opacity-30">
        <img
          src={HERO_IMG}
          alt=""
          className="w-full h-full object-cover"
          loading="eager"
        />
      </div>

      <div className="relative container py-20 md:py-32 lg:py-40">
        <div className="max-w-4xl">
          {/* Label */}
          <motion.div
            initial={{ x: -100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="inline-block bg-white text-black px-4 py-1 font-mono text-xs tracking-widest mb-6"
          >
            "PREMIUM GAME TOP-UP SERVICE"
          </motion.div>

          {/* Main Heading */}
          <motion.h1
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="font-heading font-bold text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tight leading-[0.9] mb-6"
          >
            PLAY
            <span className="text-white/40">X</span>
            WORLD
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="text-white/70 text-lg md:text-xl max-w-xl mb-8 leading-relaxed"
          >
            {t("hero.subtitle")}
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-wrap gap-4"
          >
            <Link href="/topup" className="btn-brutal bg-white text-black text-base px-8 py-4">
              {t("hero.cta1")}
            </Link>
            <Link href="/games" className="btn-brutal bg-transparent text-white border-white text-base px-8 py-4">
              {t("hero.cta2")}
            </Link>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="mt-12 flex gap-8 md:gap-12"
          >
            {[
              { num: t("hero.stats.users"), label: t("hero.stats.usersLabel") },
              { num: t("hero.stats.games"), label: t("hero.stats.gamesLabel") },
              { num: t("hero.stats.service"), label: t("hero.stats.serviceLabel") },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="font-heading font-bold text-2xl md:text-3xl">{stat.num}</div>
                <div className="text-white/50 text-sm font-mono">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Diagonal stripe bottom */}
      <div className="h-4 bg-[repeating-linear-gradient(-45deg,transparent,transparent_10px,white_10px,white_12px)]" />
    </section>
  );
}

function FeaturedGamesSection() {
  const { t } = useTranslation();
  const { ref, isInView } = useInView(0.1);

  // Fetch games from API — show specific featured games for Home page
  const { data: gamesData, isLoading } = trpc.games.list.useQuery();
  const allGames = gamesData?.games ?? [];

  // Featured game keys for Home page (user-selected 8 games)
  const featuredGameKeys = [
    "ARENABREAKOUT",
    "ZEPETO",
    "BLOODSTRIKE",
    "IDENTITYV",
    "GODDESSOFVICTORY",
    "GENSHINIMPACT",
    "HONKAISTAIRRAIL",
    "ZENLESSZONEZERO"
  ];

  const games = allGames.filter(g =>
    featuredGameKeys.includes(g.gameKey?.toUpperCase())
  ).sort((a, b) => {
    // Sort by featured order
    const aIndex = featuredGameKeys.indexOf(a.gameKey?.toUpperCase() || "");
    const bIndex = featuredGameKeys.indexOf(b.gameKey?.toUpperCase() || "");
    return aIndex - bIndex;
  }).slice(0, 8);

  return (
    <section ref={ref} className="bg-white text-black py-16 md:py-24">
      <div className="container">
        {/* Section Header */}
        <div className="flex items-end justify-between mb-12 border-b-3 border-black pb-4">
          <div>
            <span className="font-mono text-xs tracking-widest text-black/50">"RECOMMEND"</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl tracking-tight mt-1">
              {t("home.recommendTitle")}
            </h2>
          </div>
          <Link href="/games" className="font-heading font-semibold text-sm tracking-wider flex items-center gap-2 hover:gap-4 transition-all border-b-2 border-black pb-1">
            {t("home.viewAll")} <ArrowRight size={16} />
          </Link>
        </div>

        {/* Game Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-black/30 mr-2" />
            <span className="font-mono text-sm text-black/40">{t("common.loadingGames")}</span>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
            {games.map((game, i) => (
              <motion.div
                key={game.id}
                initial={{ y: 40, opacity: 0 }}
                animate={isInView ? { y: 0, opacity: 1 } : {}}
                transition={{ duration: 0.4, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                <Link href={`/topup/${game.gameKey}`} className="block group">
                  <div className="card-brutal bg-white overflow-hidden">
                    <div className="aspect-square overflow-hidden bg-black/5">
                      {(game as any).storageIconUrl || game.image ? (
                        <img
                          src={(game as any).storageIconUrl || game.image}
                          alt={game.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Gamepad2 size={32} className="text-black/20" />
                        </div>
                      )}
                    </div>
                    <div className="p-3 border-t-3 border-black">
                      <div className="font-mono text-[10px] tracking-widest text-black/50 mb-1">
                        {game.category || game.gameKey}
                      </div>
                      <h3 className="font-heading font-bold text-sm tracking-tight leading-tight line-clamp-2">
                        {game.name}
                      </h3>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function FeaturesSection() {
  const { t } = useTranslation();
  const { ref, isInView } = useInView(0.1);

  const features = [
    {
      icon: Zap,
      title: t("trust.features.speed.title"),
      desc: t("trust.features.speed.desc"),
      image: SPEED_IMG,
    },
    {
      icon: Shield,
      title: t("trust.features.security.title"),
      desc: t("trust.features.security.desc"),
      image: SECURE_IMG,
    },
    {
      icon: Star,
      title: t("trust.features.price.title"),
      desc: t("trust.features.price.desc"),
      image: null,
    },
    {
      icon: Clock,
      title: t("trust.features.support.title"),
      desc: t("trust.features.support.desc"),
      image: null,
    },
  ];

  return (
    <section ref={ref} className="bg-black text-white py-16 md:py-24">
      <div className="container">
        {/* Section Header */}
        <div className="mb-12 border-b-3 border-white pb-4">
          <span className="font-mono text-xs tracking-widest text-white/50">"WHY CHOOSE US"</span>
          <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl tracking-tight mt-1">
            {t("home.whyChooseUsTitle")}
          </h2>
        </div>

        {/* Features Grid - Editorial Magazine Style */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {features.map((feat, i) => (
            <motion.div
              key={feat.title}
              initial={{ x: i % 2 === 0 ? -60 : 60, opacity: 0 }}
              animate={isInView ? { x: 0, opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="border-3 border-white p-6 md:p-8 flex flex-col gap-4 group hover:bg-white hover:text-black transition-colors duration-200"
            >
              <div className="flex items-start justify-between">
                <div className="w-14 h-14 border-3 border-current flex items-center justify-center">
                  <feat.icon size={28} />
                </div>
                <span className="font-heading font-bold text-5xl text-current/10">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              {feat.image && (
                <div className="w-full h-32 overflow-hidden border-2 border-current">
                  <img src={feat.image} alt={feat.title} className="w-full h-full object-cover" loading="lazy" />
                </div>
              )}
              <h3 className="font-heading font-bold text-xl md:text-2xl tracking-tight">
                {feat.title}
              </h3>
              <p className="text-current/60 leading-relaxed text-sm">
                {feat.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  const { t } = useTranslation();
  const { ref, isInView } = useInView(0.1);

  const steps = [
    { num: "01", title: t("howItWorks.steps.selectGame.title"), desc: t("howItWorks.steps.selectGame.desc") },
    { num: "02", title: t("howItWorks.steps.enterID.title"), desc: t("howItWorks.steps.enterID.desc") },
    { num: "03", title: t("howItWorks.steps.selectPackage.title"), desc: t("howItWorks.steps.selectPackage.desc") },
    { num: "04", title: t("howItWorks.steps.pay.title"), desc: t("howItWorks.steps.pay.desc") },
  ];

  return (
    <section ref={ref} className="bg-white text-black py-16 md:py-24">
      <div className="container">
        {/* Section Header */}
        <div className="mb-12 border-b-3 border-black pb-4">
          <span className="font-mono text-xs tracking-widest text-black/50">"HOW IT WORKS"</span>
          <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl tracking-tight mt-1">
            {t("howItWorks.title")}
          </h2>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, i) => (
            <motion.div
              key={step.num}
              initial={{ y: 60, opacity: 0 }}
              animate={isInView ? { y: 0, opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="card-brutal bg-white p-6">
                <div className="font-heading font-bold text-6xl md:text-7xl text-black/10 leading-none mb-4">
                  {step.num}
                </div>
                <h3 className="font-heading font-bold text-lg tracking-tight mb-2">
                  {step.title}
                </h3>
                <p className="text-black/60 text-sm leading-relaxed">
                  {step.desc}
                </p>
              </div>
              {/* Arrow connector (hidden on last) */}
              {i < steps.length - 1 && (
                <div className="hidden lg:flex absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                  <ArrowRight size={20} className="text-black" />
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyPlayXworldSection() {
  const { t } = useTranslation();
  const { ref, isInView } = useInView(0.1);

  const benefits = [
    {
      icon: "⚡",
      title: t("home.benefits.instant.title"),
      desc: t("home.benefits.instant.desc"),
    },
    {
      icon: "💳",
      title: t("home.benefits.noWallet.title"),
      desc: t("home.benefits.noWallet.desc"),
    },
    {
      icon: "🔒",
      title: t("home.benefits.secure.title"),
      desc: t("home.benefits.secure.desc"),
    },
    {
      icon: "✓",
      title: t("home.benefits.oneTime.title"),
      desc: t("home.benefits.oneTime.desc"),
    },
  ];

  return (
    <section ref={ref} className="bg-white text-black py-16 md:py-24">
      <div className="container">
        {/* Section Header */}
        <div className="mb-12 border-b-3 border-black pb-4">
          <span className="font-mono text-xs tracking-widest text-black/50">"TRUST PLAYXWORLD"</span>
          <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl tracking-tight mt-1">
            {t("home.trustTitle")}
          </h2>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {benefits.map((benefit, i) => (
            <motion.div
              key={benefit.title}
              initial={{ y: 40, opacity: 0 }}
              animate={isInView ? { y: 0, opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="card-brutal bg-white border-3 border-black p-6 md:p-8 flex gap-4"
            >
              <div className="text-4xl flex-shrink-0">{benefit.icon}</div>
              <div className="flex-1">
                <h3 className="font-heading font-bold text-lg tracking-tight mb-2">
                  {benefit.title}
                </h3>
                <p className="text-black/60 text-sm leading-relaxed">
                  {benefit.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const { t } = useTranslation();
  const { ref, isInView } = useInView(0.1);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      q: t("home.faqs.delivery.q"),
      a: t("home.faqs.delivery.a"),
    },
    {
      q: t("home.faqs.refund.q"),
      a: t("home.faqs.refund.a"),
    },
    {
      q: t("home.faqs.wrongId.q"),
      a: t("home.faqs.wrongId.a"),
    },
    {
      q: t("home.faqs.wallet.q"),
      a: t("home.faqs.wallet.a"),
    },
  ];

  return (
    <section ref={ref} className="bg-black text-white py-16 md:py-24">
      <div className="container">
        {/* Section Header */}
        <div className="mb-12 border-b-3 border-white pb-4">
          <span className="font-mono text-xs tracking-widest text-white/50">"FAQ"</span>
          <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl tracking-tight mt-1">
            {t("home.faqTitle")}
          </h2>
        </div>

        {/* FAQ Accordion */}
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, i) => (
            <motion.div
              key={i}
              initial={{ y: 20, opacity: 0 }}
              animate={isInView ? { y: 0, opacity: 1 } : {}}
              transition={{ duration: 0.4, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="border-2 border-white/30 overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full p-6 flex items-center justify-between hover:bg-white/5 transition-colors"
              >
                <span className="font-heading font-bold text-lg text-left">{faq.q}</span>
                <ChevronDown
                  size={20}
                  className={`flex-shrink-0 transition-transform ${
                    openIndex === i ? "rotate-180" : ""
                  }`}
                />
              </button>
              {openIndex === i && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="border-t-2 border-white/30 px-6 py-4 bg-white/5"
                >
                  <p className="text-white/70 leading-relaxed">{faq.a}</p>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>

        {/* More Link */}
        <div className="mt-12 text-center">
          <Link
            href="/support"
            className="inline-flex items-center gap-2 font-heading font-semibold text-white hover:text-white/70 transition-colors border-b-2 border-white pb-1"
          >
            {t("home.faqMore")} <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  const { t } = useTranslation();
  return (
    <section className="bg-black text-white relative overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0 opacity-20">
        <img src={TOPUP_IMG} alt="" className="w-full h-full object-cover" loading="lazy" />
      </div>

      <div className="relative container py-20 md:py-28 text-center">
        <div className="inline-block bg-white text-black px-4 py-1 font-mono text-xs tracking-widest mb-6">
          "START NOW"
        </div>
        <h2 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight mb-6">
          {t("home.ctaTitle")}
        </h2>
        <p className="text-white/60 text-lg max-w-xl mx-auto mb-8">
          {t("home.ctaSubtitle")}
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link href="/topup" className="btn-brutal bg-white text-black text-base px-10 py-4">
            <Gamepad2 size={20} className="mr-2" />
            {t("hero.cta1")}
          </Link>
        </div>
      </div>

      {/* Diagonal stripe top */}
      <div className="h-4 bg-[repeating-linear-gradient(45deg,transparent,transparent_10px,white_10px,white_12px)]" />
    </section>
  );
}

export default function Home() {
  return (
    <Layout>
      <HeroSection />
      <FeaturedGamesSection />
      <FeaturesSection />
      <HowItWorksSection />
      <WhyPlayXworldSection />
      <FAQSection />
      <CTASection />
    </Layout>
  );
}
